import React, { useCallback, useEffect, useState } from "react";
import { TrashIcon, ShieldCheckIcon } from "@heroicons/react/24/outline";
import { useAuthFetch } from "../../hooks/useAuthFetch";
import { Table } from "../../components/table";
import { DeleteConfirm } from "../../components/modal/deleteConfirm";
import { PostDetails } from "./postDetails";

const TYPE_LABELS = { fixed: "Fixed", roving: "Roving", mobile: "Mobile patrol" };

// Posts for one site (`siteId`), or the org's mobile patrol routes (`mobile`).
// The parent owns the "create" button: `editing` is null (sheet closed), {}
// (create), or a post (edit); `setEditing` opens and closes the sheet.
function Posts({ siteId, mobile, editing, setEditing }) {
  const authFetch = useAuthFetch();
  const [posts, setPosts] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const fetchPosts = useCallback(async () => {
    setIsLoading(true);
    const query = mobile ? "type=mobile" : `site_id=${siteId}`;
    try {
      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/v1/posts?${query}`
      );
      if (response.ok) setPosts(await response.json());
      else console.error("Error fetching posts");
    } catch (error) {
      console.error("Error fetching posts", error);
    } finally {
      setIsLoading(false);
    }
  }, [siteId, mobile, authFetch]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const deletePost = async (id) => {
    const response = await authFetch(
      `${process.env.REACT_APP_API_URL}/v1/posts/delete/${id}`,
      { method: "PUT" }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || "Failed to delete");
    }
  };

  // A site always keeps at least one post (the server enforces it too).
  const canDelete = mobile || (posts && posts.length > 1);

  const columns = React.useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        Cell: ({ row }) => (
          <button
            type="button"
            onClick={() => setEditing(row.original)}
            className="text-left text-gray-800 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white hover:underline cursor-pointer"
          >
            {row.values.name}
          </button>
        ),
      },
      {
        Header: "Type",
        accessor: (post) => TYPE_LABELS[post.type] || post.type,
        id: "type",
      },
      {
        Header: "Armed",
        accessor: (post) => (post.is_armed ? "Yes" : "No"),
        id: "armed",
      },
      {
        Header: "Description",
        accessor: "description",
      },
      {
        Header: "",
        id: "delete",
        accessor: () => "delete",
        Cell: ({ row }) =>
          canDelete ? (
            <button
              onClick={() =>
                setDeleteItem({ identifier: row.original.name, id: row.original.id })
              }
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors duration-150"
            >
              <TrashIcon className="h-4 w-4 shrink-0" />
            </button>
          ) : null,
      },
    ],
    [canDelete, setEditing]
  );

  return (
    <div>
      <Table
        data={posts || []}
        columns={columns}
        isLoading={isLoading && posts === undefined}
        emptyState={
          <div className="flex flex-col items-center justify-center h-72">
            <ShieldCheckIcon className="h-10 w-10 text-gray-300 dark:text-gray-600" />
            <p className="mt-3 text-sm text-gray-400 dark:text-gray-500 text-center">
              {mobile ? "No patrol routes yet" : "No posts yet"}
            </p>
          </div>
        }
      />
      <PostDetails
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        post={editing && editing.id ? editing : null}
        siteId={siteId}
        mobile={mobile}
        onSaved={fetchPosts}
      />
      <DeleteConfirm
        isOpen={!!deleteItem}
        toggleOpen={() => setDeleteItem(null)}
        selectedDeleteItem={deleteItem}
        handleDeleteSuccess={fetchPosts}
        onDelete={deletePost}
      />
    </div>
  );
}

export { Posts };
